import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';

export interface ClothingItem {
  id: string;
  user_id: string;
  image_url: string;
  category: string;
  subcategory?: string;
  color?: string;
  brand?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateClothingItemRequest {
  category: string;
  subcategory?: string;
  color?: string;
  brand?: string;
  notes?: string;
}

// Helper to convert Firestore timestamp to ISO string
const timestampToISO = (timestamp: Timestamp | Date | string | undefined): string => {
  if (!timestamp) return new Date().toISOString();
  if (timestamp instanceof Date) return timestamp.toISOString();
  if (typeof timestamp === 'string') return timestamp;
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  return new Date().toISOString();
};

// Helper to convert Firestore document to ClothingItem
const docToClothingItem = (docData: any): ClothingItem => ({
  id: docData.id,
  user_id: docData.user_id,
  image_url: docData.image_url,
  category: docData.category,
  subcategory: docData.subcategory,
  color: docData.color,
  brand: docData.brand,
  notes: docData.notes,
  created_at: timestampToISO(docData.created_at),
  updated_at: timestampToISO(docData.updated_at),
});

export const clothingService = {
  getAll: async (): Promise<ClothingItem[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const q = query(collection(db, 'clothing'), where('user_id', '==', user.uid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => docToClothingItem({ id: doc.id, ...doc.data() }));
  },

  getById: async (id: string): Promise<ClothingItem> => {
    const docRef = doc(db, 'clothing', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Clothing item not found');
    return docToClothingItem({ id: docSnap.id, ...docSnap.data() });
  },

  create: async (file: File, data: CreateClothingItemRequest): Promise<ClothingItem> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Upload image to Firebase Storage
    const storageRef = ref(storage, `uploads/${user.uid}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const imageUrl = await getDownloadURL(storageRef);

    // Create document in Firestore
    const docRef = await addDoc(collection(db, 'clothing'), {
      user_id: user.uid,
      image_url: imageUrl,
      category: data.category,
      subcategory: data.subcategory || null,
      color: data.color || null,
      brand: data.brand || null,
      notes: data.notes || null,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return docToClothingItem({ id: docSnap.id, ...docSnap.data() });
  },

  update: async (id: string, data: Partial<CreateClothingItemRequest>): Promise<ClothingItem> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = doc(db, 'clothing', id);
    const updateData: any = {
      updated_at: serverTimestamp(),
    };

    if (data.category !== undefined) updateData.category = data.category;
    if (data.subcategory !== undefined) updateData.subcategory = data.subcategory || null;
    if (data.color !== undefined) updateData.color = data.color || null;
    if (data.brand !== undefined) updateData.brand = data.brand || null;
    if (data.notes !== undefined) updateData.notes = data.notes || null;

    await updateDoc(docRef, updateData);
    const docSnap = await getDoc(docRef);
    return docToClothingItem({ id: docSnap.id, ...docSnap.data() });
  },

  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'clothing', id));
  },

  rotate: async (id: string): Promise<void> => {
    // Rotation is handled by Cloud Function via callable API
    // Note: This function is only available during upload, not after
    throw new Error('Image rotation is only available during upload');
  },
};
